import React, { useEffect } from "react";
import JSONView from "../components/JSONView";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGameId,
  selectGameObject,
  selectGameStatusObject,
} from "features/gamestate/gameStateSlice";
import { selectAllPlayers } from "features/player/playerSlice";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { StateButton, EndRoundButton } from "../components/GameStateButtons";
import GameStateChip from "../components/GameStateChip";
import JudgingBoard from "../components/JudgingBoard";
import MiniGameBoard from "../components/MiniGameBoard";
import AdminLeaderboard from "../components/AdminLeaderboard";
import AdminActions from "../components/AdminActions";
import WagerMonitor from "../components/WagerMonitor";
import { MANAGER_QUESTIONVIEW_ROUNDSTATES } from "../../gamestate/constants";
import { selectCurrentQuestionObject } from "../../question/questionSlice";
import {
  stopWatchingQuestionSubmissionsAction,
  watchQuestionSubmissionsAction,
} from "../../question/submissionSlice";
import { selectHostState } from "features/host/hostStateSlice";

const GameControllerV2 = () => {
  const dispatch = useDispatch();
  const gameObj = useSelector(selectGameObject);
  const gameStateObj = useSelector(selectGameStatusObject);
  const gameId = useSelector(selectGameId);
  const questionId = gameStateObj.questionId;
  const questionObject = useSelector(selectCurrentQuestionObject);
  const hostState = useSelector(selectHostState);
  const players = useSelector(selectAllPlayers);

  useEffect(() => {
    if (gameId) {
      Analytics.track(EVENTS.CONTROLLER_OPENED, {
        game_id: gameId,
        has_players: players.length > 0,
        player_count: players.length,
        current_state: gameStateObj.currentState,
        round_state: gameStateObj.roundState,
        current_round: gameStateObj.round,
      });
    }
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (gameId && questionId) {
      dispatch(watchQuestionSubmissionsAction(gameId, questionId));
    }
    return function cleanup() {
      if (gameId && questionId) {
        dispatch(stopWatchingQuestionSubmissionsAction(gameId, questionId));
      }
    };
  }, [dispatch, questionId, gameId]);

  const showJudgingBoard = MANAGER_QUESTIONVIEW_ROUNDSTATES.includes(gameStateObj.roundState);
  const gameTitle = gameObj?.name || "Game Controller";

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" sx={{ fontFamily: "Helvetica Inserat" }}>
              {gameTitle}
            </Typography>
            {gameObj?.roomId && (
              <Link
                href={`/game/${gameObj.roomId}`}
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                Player view: {gameObj.roomId}
                <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
              </Link>
            )}
          </Box>
          <GameStateChip />
          <StateButton />
          <EndRoundButton />
        </Stack>
      </Box>

      <Divider />

      {/* Main Content: Mini Gameboard + Sidebar */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={7}>
          <Typography variant="h6" gutterBottom>Board</Typography>
          <MiniGameBoard />
        </Grid>
        <Grid item xs={12} md={5}>
          <AdminLeaderboard />
          <WagerMonitor />
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>Admin Actions</Typography>
          <AdminActions />
        </Grid>
      </Grid>

      {/* Judging Board */}
      {showJudgingBoard && (
        <Box sx={{ mt: 2 }}>
          <Divider />
          <JudgingBoard />
        </Box>
      )}

      {/* Debug Section */}
      <Box sx={{ mt: 2 }}>
        <Divider />
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Debug</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Game State</Typography>
                <JSONView gameObject={gameStateObj} />
              </Grid>
              <Grid item xs={12} md={4}>
                {showJudgingBoard && questionObject && (
                  <>
                    <Typography variant="subtitle2">Question</Typography>
                    <JSONView gameObject={questionObject} />
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Host State</Typography>
                <JSONView gameObject={hostState} />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
};

export default GameControllerV2;
